import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Loader2, Users, ListChecks, BookOpen, FileCheck, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-5 rounded-md border border-border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div className="font-mono text-3xl font-bold">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-analytics"],
    queryFn: async () => (await api.get("/admin/analytics")).data,
  });

  if (isLoading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const d = data || {};
  const axisStyle = { fontSize: 11, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Admin</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Analytics</h1>
        </div>
        <Link to="/admin/generate">
          <Button className="rounded-md gap-2" data-testid="goto-generator"><Sparkles className="w-4 h-4" /> AI Generator</Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Students" value={d.total_students ?? 0} testid="stat-students" />
        <Stat icon={ListChecks} label="Quizzes" value={d.total_quizzes ?? 0} testid="stat-quizzes" />
        <Stat icon={BookOpen} label="Courses" value={d.total_courses ?? 0} testid="stat-courses" />
        <Stat icon={FileCheck} label="Submissions" value={d.total_submissions ?? 0} testid="stat-submissions" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4">Category Performance</h3>
          {d.category_perf?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.category_perf}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="category" tick={axisStyle} />
                <YAxis domain={[0, 100]} tick={axisStyle} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Bar dataKey="avg" name="Avg Score %" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-12 text-center">No submission data yet.</p>
          )}
        </div>

        <div className="p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4">Recent Submissions</h3>
          {d.recent_submissions?.length ? (
            <div className="space-y-2" data-testid="admin-recent">
              {d.recent_submissions.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.quiz_title}</p>
                    <p className="text-xs text-muted-foreground font-mono">{s.category}</p>
                  </div>
                  <span className={`font-mono font-bold text-sm ${s.score >= 75 ? "text-success" : s.score >= 50 ? "text-chart-4" : "text-destructive"}`}>{s.score}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No submissions yet.</p>
          )}
        </div>
      </div>

      <div className="p-6 rounded-md border border-border bg-card">
        <h3 className="font-heading font-bold text-lg mb-4">Students ({d.students?.length ?? 0})</h3>
        {d.students?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="students-table">
              <thead>
                <tr className="text-left text-muted-foreground font-mono text-xs uppercase tracking-wider border-b border-border">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2">Target Path</th>
                </tr>
              </thead>
              <tbody>
                {d.students.map((s) => (
                  <tr key={s.id} className="border-b border-border last:border-0">
                    <td className="py-2.5 pr-4 font-medium">{s.name}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{s.email}</td>
                    <td className="py-2.5"><span className="font-mono text-xs px-2 py-0.5 rounded bg-secondary">{s.target_path || "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No students registered yet.</p>
        )}
      </div>
    </div>
  );
}
